/**
 * 1€ Filter (One Euro Filter) - Signal Stabilization (REQ-SIG-01)
 * 
 * The 1€ Filter is the industry standard for HCI because it is adaptive:
 * - Jitters less at low speeds (stable cursor when looking at buttons)
 * - Lags less at high speeds (responsive cursor when moving quickly)
 * 
 * @requirements REQ-SIG-01: Adaptive smoothing with configurable minCutoff and beta
 * 
 * Parameters:
 * - minCutoff (fc_min): Minimum bandwidth. Lower = smoother but laggy. Default: 1.0 Hz
 * - beta (β): Speed coefficient. Higher = less lag during fast movement. Default: 0.007
 * 
 * References:
 * - Casiez, G., Roussel, N., & Vogel, D. (2012). 1€ filter: a simple speed-based low-pass filter
 */

class LowPassFilter {
  private y: number | null = null;
  private s: number | null = null;

  alpha(cutoff: number, dt: number): number {
    const tau = 1.0 / (2 * Math.PI * cutoff);
    return 1.0 / (1.0 + tau / dt);
  }

  filter(value: number, alpha: number): number {
    if (this.y === null) {
      this.s = value;
      this.y = value;
    } else {
      this.s = alpha * value + (1 - alpha) * (this.s ?? value);
      this.y = this.s;
    }
    return this.y;
  }

  reset(): void {
    this.y = null;
    this.s = null;
  }

  lastValue(): number | null {
    return this.y;
  }
}

export class OneEuroFilter {
  private minCutoff: number;
  private beta: number;
  private dcutoff: number;
  
  private xFilter: LowPassFilter;
  private dxFilter: LowPassFilter;
  
  private lastTimestamp: number = 0;
  private initialized: boolean = false;

  /**
   * Create a 1€ Filter
   * @param minCutoff - Minimum cutoff frequency (Hz). Lower = smoother. Default: 1.0
   * @param beta - Speed coefficient. Higher = less lag during fast movement. Default: 0.007
   * @param dcutoff - Derivative cutoff frequency. Usually 1.0. Default: 1.0
   */
  constructor(minCutoff: number = 1.0, beta: number = 0.007, dcutoff: number = 1.0) {
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.dcutoff = dcutoff;
    
    this.xFilter = new LowPassFilter();
    this.dxFilter = new LowPassFilter();
  }

  /**
   * Filter a single value
   * @param value - Raw input value
   * @param timestamp - Current timestamp in milliseconds
   * @returns Filtered value
   */
  filter(value: number, timestamp: number): number {
    if (!this.initialized) {
      this.lastTimestamp = timestamp;
      this.initialized = true;
      // Initialize filters with first value
      this.xFilter.filter(value, 1.0);
      this.dxFilter.filter(0, 1.0);
      return value;
    }

    // Calculate time delta in seconds
    const dt = Math.max((timestamp - this.lastTimestamp) / 1000, 0.001);
    this.lastTimestamp = timestamp;

    // Estimate velocity (derivative)
    const prevX = this.xFilter.lastValue() ?? value;
    const dx = (value - prevX) / dt;
    
    // Filter the derivative
    const edx = this.dxFilter.filter(dx, this.dxFilter.alpha(this.dcutoff, dt));

    // Use velocity to adjust cutoff frequency
    // Higher velocity = higher cutoff = less smoothing = more responsive
    const cutoff = this.minCutoff + this.beta * Math.abs(edx);

    // Filter the position with adaptive cutoff
    return this.xFilter.filter(value, this.xFilter.alpha(cutoff, dt));
  }

  /**
   * Reset the filter state
   */
  reset(): void {
    this.xFilter.reset();
    this.dxFilter.reset();
    this.initialized = false;
    this.lastTimestamp = 0;
  }

  /**
   * Update filter parameters dynamically
   * Useful for adjusting based on tracking confidence
   */
  setParameters(minCutoff?: number, beta?: number): void {
    if (minCutoff !== undefined) this.minCutoff = minCutoff;
    if (beta !== undefined) this.beta = beta;
  }

  /**
   * Get current parameters
   */
  getParameters(): { minCutoff: number; beta: number; dcutoff: number } {
    return {
      minCutoff: this.minCutoff,
      beta: this.beta,
      dcutoff: this.dcutoff,
    };
  }
}

/**
 * 2D One Euro Filter for cursor position (x, y)
 */
export class OneEuroFilter2D {
  private xFilter: OneEuroFilter;
  private yFilter: OneEuroFilter;

  constructor(minCutoff: number = 1.0, beta: number = 0.007, dcutoff: number = 1.0) {
    this.xFilter = new OneEuroFilter(minCutoff, beta, dcutoff);
    this.yFilter = new OneEuroFilter(minCutoff, beta, dcutoff);
  }

  /**
   * Filter a 2D position
   */
  filter(x: number, y: number, timestamp: number): { x: number; y: number } {
    return {
      x: this.xFilter.filter(x, timestamp),
      y: this.yFilter.filter(y, timestamp),
    };
  }

  /**
   * Reset both filters
   */
  reset(): void {
    this.xFilter.reset();
    this.yFilter.reset();
  }

  /**
   * Update parameters on both axes
   */
  setParameters(minCutoff?: number, beta?: number): void {
    this.xFilter.setParameters(minCutoff, beta);
    this.yFilter.setParameters(minCutoff, beta);
  }

  /**
   * Auto-tune based on tracking confidence
   * Low confidence = more smoothing (lower minCutoff)
   * 
   * @param confidence - Tracking confidence 0-1
   */
  autoTune(confidence: number): void {
    // When confidence is low (< 0.5), decrease minCutoff to stabilize shaky cursor
    // When confidence is high, use normal parameters
    const adjustedMinCutoff = confidence < 0.5 
      ? 0.3 + (confidence * 1.4) // 0.3 to 1.0 range for low confidence
      : 1.0 + (confidence - 0.5) * 2.0; // 1.0 to 2.0 range for high confidence
    
    this.setParameters(adjustedMinCutoff);
  }
}

/**
 * Map user-facing "Smoothness" slider (1-10) to filter parameters
 * 1 = very responsive (low smoothing)
 * 10 = very smooth (high smoothing)
 * 
 * @requirements REQ-SIG-01: Do not expose math values to user. Map to simple slider.
 */
export function mapSmoothnessToFilterParams(smoothness: number): { minCutoff: number; beta: number } {
  // Clamp to 1-10 range
  const s = Math.max(1, Math.min(10, smoothness));
  
  // minCutoff: Higher smoothness = lower cutoff = more smoothing
  // Range: 3.0 (responsive) to 0.3 (smooth)
  const minCutoff = 3.0 - (s - 1) * 0.3;
  
  // beta: Higher smoothness = lower beta = more lag tolerance
  // Range: 0.02 (responsive) to 0.002 (smooth)
  const beta = 0.02 - (s - 1) * 0.002;
  
  return { minCutoff, beta };
}
